import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { FetchTitleListYTDSalesRecord } from "../../../../redux/reducers/titleListYTDSalesReducer";
import { FetchTitleListMTDSalesRecord } from "../../../../redux/reducers/titleListMTDSalesReducer";
import { FetchTitleListLTDSalesRecord } from "../../../../redux/reducers/titleListLTDSalesReducer";
import { setTitleSalesYearMonthData, setTitleSalesLTDData, setTitleSalesYTDData } from "../setSalesDataCommon";
import moment from "moment";

interface SalesDataStructure {
  gross: { units: string; dollars: string };
  returns: { units: string; dollars: string };
  net: { units: string; dollars: string };
  sellThrough: string | number;
}

interface StatsContainerProps {
  tabs: string[];
  selectedYear: string | null;
  selectedMonth: string | null;
}

const StatsContainer: React.FC<StatsContainerProps> = ({ tabs, selectedYear, selectedMonth }) => {
  const [selectedTab, setSelectedTab] = useState<string>("YTD");
  const [statsData, setStatsData] = useState<SalesDataStructure | null>(null);
  const YTDSales = useSelector((state: RootState) => state.titleSalesYTDReducer.data);
  const MTDSales = useSelector((state: RootState) => state.titleSalesMTDReducer.data);
  const LTDSales = useSelector((state: RootState) => state.titleSalesLTDReducer.data);
  const DetailData = useSelector((state: RootState) => state.detailPageReducer.data);
  const dispatch = useDispatch<AppDispatch>();

  // Fetch data based on selected tab
  useEffect(() => {
    if (DetailData.EAN) {
      if (selectedTab === "YTD") {
        dispatch(FetchTitleListYTDSalesRecord({ SelectedEbookIsbn: DetailData.EAN }));
      } else if (selectedTab === "MTD") {
        dispatch(FetchTitleListMTDSalesRecord({ SelectedEbookIsbn: DetailData.EAN }));
      } else if (selectedTab === "LTD") {
        dispatch(FetchTitleListLTDSalesRecord({ SelectedEbookIsbn: DetailData.EAN, selectedYear, selectedMonth }));
      }
    }
  }, [selectedTab, DetailData.EAN, selectedYear, selectedMonth, dispatch]);

  useEffect(() => {
    setStatsData(null);
    
    if (selectedTab === "YTD" && YTDSales) {
      setTitleSalesYTDData(setStatsData, YTDSales);
    } else if (selectedTab === "MTD" && MTDSales) {
      setTitleSalesYearMonthData(setStatsData, MTDSales);
    } else if (selectedTab === "LTD" && LTDSales) {
      setTitleSalesLTDData(setStatsData, LTDSales);
    }
  }, [selectedTab, YTDSales, MTDSales, LTDSales]);

  // Render sales table matching screenshot format
  const renderSalesTable = (title: string, period: string) => {
    if (!statsData) return null;

    const sellThroughLabel = `SELL THROUGH ${period}`;

    return (
      <div className="w-full flex flex-col bg-gray-200 border-2 border-black p-1">
        <div className="font-bold text-black py-0.5 mb-0.5 text-sm">
          {title}
        </div>
        <div className="w-full border-2">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="px-2 py-0.5 text-center font-semibold text-gray-800 text-sm">Gross Sales</th>
                <th className="px-2 py-0.5 text-center font-semibold text-gray-800 text-sm">Returns</th>
                <th className="px-2 py-0.5 text-center font-semibold text-gray-800 text-sm">Net Sales</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-2 py-0.5 text-center">
                  <div className="flex flex-row items-center justify-center gap-2">
                    <span className="text-[#e31c23] font-semibold text-sm">Units</span>
                    <span className="text-base font-normal text-gray-800">{statsData.gross.units}</span>
                  </div>
                </td>
                <td className="px-2 py-0.5 text-center">
                  <div className="flex flex-row items-center justify-center gap-2">
                    <span className="text-[#e31c23] font-semibold text-sm">Units</span>
                    <span className="text-base font-normal text-gray-800">{statsData.returns.units}</span>
                  </div>
                </td>
                <td className="px-2 py-0.5 text-center">
                  <div className="flex flex-row items-center justify-center gap-2">
                    <span className="text-[#e31c23] font-semibold text-sm">Units</span>
                    <span className="text-base font-normal text-gray-800">{statsData.net.units}</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td className="px-2 py-0.5 text-center">
                  <div className="flex flex-row items-center justify-center gap-2">
                    <span className="text-[#e31c23] font-semibold text-sm">$ Dollars</span>
                    <span className="text-base font-normal text-gray-800">{statsData.gross.dollars}</span>
                  </div>
                </td>
                <td className="px-2 py-0.5 text-center">
                  <div className="flex flex-row items-center justify-center gap-2">
                    <span className="text-[#e31c23] font-semibold text-sm">$ Dollars</span>
                    <span className="text-base font-normal text-gray-800">{statsData.returns.dollars}</span>
                  </div>
                </td>
                <td className="px-2 py-0.5 text-center">
                  <div className="flex flex-row items-center justify-center gap-2">
                    <span className="text-[#e31c23] font-semibold text-sm">$ Dollars</span>
                    <span className="text-base font-normal text-gray-800">{statsData.net.dollars}</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td colSpan={3} className="px-2 py-1.5 text-center">
                  <div className="flex flex-row items-center justify-center gap-2">
                    <span className="text-[#e31c23] font-semibold text-sm">{sellThroughLabel}</span>
                    <span className="text-base font-normal text-gray-800">{statsData.sellThrough}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render only the content for the selected tab
  const renderTabContent = () => {
    switch (selectedTab) {
      case "YTD":
        return renderSalesTable(`Current Year Sales: ${moment().format("YYYY")}`, "YTD");
      case "MTD":
        return renderSalesTable(`Current Month Sales: ${moment().format("MMMM")}`, "MTD");
      case "LTD":
        return renderSalesTable(`Sales: ${selectedMonth ? selectedMonth : ""} ${selectedYear ? selectedYear : "(All Years)"}`, "LTD");
      default:
        return null;
    }
  };

  return (
    <div className="w-full flex flex-col border-2 border-gray-300 bg-white p-1 mt-0 overflow-hidden">
      {/* Tab Headers */}
      <div className="w-full flex flex-row sticky top-0 bg-white z-10">
        {tabs.map((tab) => (
          <div
            key={tab}
            onClick={() => setSelectedTab(tab)}
            className={`
              flex-1 text-center font-bold cursor-pointer py-2 text-red-600
              transition-all duration-200 mb-[-2px]
              ${selectedTab === tab 
                ? "bg-gray-200 border-2 border-black border-b-0" 
                : ""}
            `}
          >
            {tab}
          </div>
        ))}
      </div>
      {/* Tab Content */}
      <div className="w-full mt-0">{renderTabContent()}</div>
    </div>
  );
};

export default StatsContainer;